import React, { createContext, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Building2, GraduationCap, Send, FileCheck, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { submitInstitution, submitStudent, submitContact, submitAdmission } from "@/lib/api";
import { SERVICES, CITIES, COURSES, EXAMS } from "@/data/site";

const ModalContext = createContext(null);
export const useModal = () => useContext(ModalContext);

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors focus:border-brand-cyan/70 focus:bg-white/[0.07]";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-white/50";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export function ModalProvider({ children }) {
  const [open, setOpen] = useState(null); // 'institution' | 'student' | 'contact' | 'admission'
  const [admissionCollege, setAdmissionCollege] = useState("");
  const value = {
    openInstitution: () => setOpen("institution"),
    openStudent: () => setOpen("student"),
    openContact: () => setOpen("contact"),
    openAdmission: (college = "General Application") => { setAdmissionCollege(college); setOpen("admission"); },
    close: () => setOpen(null),
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <InstitutionModal open={open === "institution"} onClose={() => setOpen(null)} />
      <StudentModal open={open === "student"} onClose={() => setOpen(null)} />
      <ContactModal open={open === "contact"} onClose={() => setOpen(null)} />
      <AdmissionModal open={open === "admission"} college={admissionCollege} onClose={() => setOpen(null)} />
    </ModalContext.Provider>
  );
}

function Shell({ open, onClose, icon: Icon, title, subtitle, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto border-white/10 bg-ink-1/95 p-0 backdrop-blur-2xl sm:max-w-lg"
        data-testid="form-modal"
      >
        <div className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-brand-blue/20 to-transparent p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-cyan/15 text-brand-cyan">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-white">{title}</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-white/50">{subtitle}</p>
            </div>
          </div>
        </div>
        <div className="p-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

function InstitutionModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);

  const toggle = (name) =>
    setServices((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const onSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setLoading(true);
    try {
      await submitInstitution({
        institution_name: f.get("institution_name"),
        contact_person: f.get("contact_person"),
        designation: f.get("designation"),
        phone: f.get("phone"),
        email: f.get("email"),
        city: f.get("city"),
        institution_type: f.get("institution_type"),
        current_intake: f.get("current_intake"),
        services_required: services,
        message: f.get("message"),
      });
      toast.success("Request received! Our team will reach out for your strategy call.");
      onClose();
      e.target.reset();
      setServices([]);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell open={open} onClose={onClose} icon={Building2} title="Partner With A3" subtitle="Request a strategy call for your institution">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="institution-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Institution Name"><input name="institution_name" required className={inputCls} placeholder="e.g. City Business School" data-testid="inst-name" /></Field>
          <Field label="Contact Person"><input name="contact_person" required className={inputCls} placeholder="Full name" data-testid="inst-person" /></Field>
          <Field label="Designation"><input name="designation" className={inputCls} placeholder="Director / Dean" /></Field>
          <Field label="Phone"><input name="phone" required className={inputCls} placeholder="Mobile number" data-testid="inst-phone" /></Field>
          <Field label="Email"><input name="email" type="email" required className={inputCls} placeholder="you@college.edu" data-testid="inst-email" /></Field>
          <Field label="City"><input name="city" className={inputCls} placeholder="City" /></Field>
          <Field label="Institution Type"><input name="institution_type" className={inputCls} placeholder="B-School / University" /></Field>
          <Field label="Current Student Intake"><input name="current_intake" className={inputCls} placeholder="e.g. 120 / year" /></Field>
        </div>
        <Field label="Services Required">
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button
                type="button"
                key={s.title}
                onClick={() => toggle(s.title)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  services.includes(s.title)
                    ? "border-brand-cyan bg-brand-cyan/15 text-brand-cyan"
                    : "border-white/10 bg-white/5 text-white/60 hover:border-white/25"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Message"><textarea name="message" rows={3} className={inputCls} placeholder="Tell us about your goals" /></Field>
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-60" data-testid="inst-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Request a Strategy Call
        </button>
      </form>
    </Shell>
  );
}

function StudentModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setLoading(true);
    try {
      await submitStudent({
        name: f.get("name"), mobile: f.get("mobile"), email: f.get("email"),
        city: f.get("city"), graduation: f.get("graduation"),
        graduation_percentage: f.get("graduation_percentage"),
        entrance_exam: f.get("entrance_exam"), preferred_city: f.get("preferred_city"),
        preferred_course: f.get("preferred_course"), budget: f.get("budget"),
      });
      toast.success("Great! An A3 counsellor will help you find your dream college.");
      onClose();
      e.target.reset();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell open={open} onClose={onClose} icon={GraduationCap} title="Find Your Dream College" subtitle="Share your goals — we'll guide your journey">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="student-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Name"><input name="name" required className={inputCls} placeholder="Full name" data-testid="stu-name" /></Field>
          <Field label="Mobile"><input name="mobile" required className={inputCls} placeholder="Mobile number" data-testid="stu-mobile" /></Field>
          <Field label="Email"><input name="email" type="email" required className={inputCls} placeholder="you@email.com" data-testid="stu-email" /></Field>
          <Field label="City"><input name="city" className={inputCls} placeholder="Current city" /></Field>
          <Field label="Graduation"><input name="graduation" className={inputCls} placeholder="e.g. B.Com" /></Field>
          <Field label="Graduation %"><input name="graduation_percentage" className={inputCls} placeholder="e.g. 72%" /></Field>
          <Field label="Entrance Exam">
            <select name="entrance_exam" className={inputCls}>
              <option value="">Select</option>
              {["CAT", "XAT", "CMAT", "MAT", "SNAP", "NMAT", "Other"].map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
            </select>
          </Field>
          <Field label="Preferred City">
            <select name="preferred_city" className={inputCls}>
              {CITIES.map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
            </select>
          </Field>
          <Field label="Preferred Course">
            <select name="preferred_course" className={inputCls}>
              {COURSES.filter((c) => c !== "All").map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
            </select>
          </Field>
          <Field label="Budget"><input name="budget" className={inputCls} placeholder="e.g. ₹8-10 LPA" /></Field>
        </div>
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-60" data-testid="stu-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
          Find My Dream College
        </button>
      </form>
    </Shell>
  );
}

function ContactModal({ open, onClose }) {  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setLoading(true);
    try {
      await submitContact({
        name: f.get("name"), email: f.get("email"), phone: f.get("phone"),
        subject: f.get("subject"), message: f.get("message"),
      });
      toast.success("Message sent! Our team will get back to you shortly.");
      onClose();
      e.target.reset();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell open={open} onClose={onClose} icon={Send} title="Talk to Our Team" subtitle="We usually respond within one business day">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Name"><input name="name" required className={inputCls} placeholder="Full name" data-testid="contact-name" /></Field>
          <Field label="Phone"><input name="phone" required className={inputCls} placeholder="Mobile number" data-testid="contact-phone" /></Field>
        </div>
        <Field label="Email"><input name="email" type="email" required className={inputCls} placeholder="you@email.com" data-testid="contact-email" /></Field>
        <Field label="Subject"><input name="subject" className={inputCls} placeholder="How can we help?" /></Field>
        <Field label="Message"><textarea name="message" rows={4} required className={inputCls} placeholder="Your message" data-testid="contact-message" /></Field>
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-60" data-testid="contact-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Message
        </button>
      </form>
    </Shell>
  );
}

const ADMISSION_STEPS = ["Personal", "Academics", "Preferences"];
const EMPTY_ADMISSION = {
  name: "", mobile: "", email: "", city: "", dob: "",
  graduation: "", graduation_percentage: "", work_experience: "",
  entrance_exam: "", exam_score: "", preferred_course: "MBA", intake_year: "2026", message: "",
};

function AdmissionModal({ open, college, onClose }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(EMPTY_ADMISSION);

  useEffect(() => {
    if (open) { setStep(0); setData(EMPTY_ADMISSION); }
  }, [open]);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const canNext =
    step === 0
      ? Boolean(data.name && data.mobile && /\S+@\S+\.\S+/.test(data.email))
      : step === 1
      ? Boolean(data.graduation)
      : true;

  const onSubmit = async () => {
    setLoading(true);
    try {
      await submitAdmission({ college_name: college, ...data });
      toast.success("Application submitted! Our admissions team will call you within 24 hours.");
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell open={open} onClose={onClose} icon={FileCheck} title="Admission Application" subtitle={`Applying to: ${college}`}>
      <div data-testid="admission-form">
        <div className="mb-6 flex items-center gap-2">
          {ADMISSION_STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-brand-cyan text-[#08120A]" : i === step ? "border border-brand-cyan text-brand-cyan" : "border border-white/15 text-white/40"}`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={`hidden font-mono text-[10px] uppercase tracking-wider sm:block ${i === step ? "text-brand-cyan" : "text-white/40"}`}>{s}</span>
              </div>
              {i < ADMISSION_STEPS.length - 1 && <span className="h-px flex-1 bg-white/10" />}
            </React.Fragment>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          {step === 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admission-step-personal">
              <Field label="Full Name *"><input value={data.name} onChange={set("name")} required className={inputCls} placeholder="Full name" data-testid="adm-name" /></Field>
              <Field label="Mobile *"><input value={data.mobile} onChange={set("mobile")} required className={inputCls} placeholder="Mobile number" data-testid="adm-mobile" /></Field>
              <Field label="Email *"><input value={data.email} onChange={set("email")} type="email" required className={inputCls} placeholder="you@email.com" data-testid="adm-email" /></Field>
              <Field label="Current City"><input value={data.city} onChange={set("city")} className={inputCls} placeholder="City" data-testid="adm-city" /></Field>
              <Field label="Date of Birth"><input value={data.dob} onChange={set("dob")} type="date" className={inputCls} data-testid="adm-dob" /></Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admission-step-academics">
              <Field label="Graduation *"><input value={data.graduation} onChange={set("graduation")} className={inputCls} placeholder="e.g. B.Com" data-testid="adm-graduation" /></Field>
              <Field label="Graduation %"><input value={data.graduation_percentage} onChange={set("graduation_percentage")} className={inputCls} placeholder="e.g. 72%" data-testid="adm-grad-pct" /></Field>
              <Field label="Entrance Exam">
                <select value={data.entrance_exam} onChange={set("entrance_exam")} className={inputCls} data-testid="adm-exam">
                  <option value="" className="bg-ink-1">Select</option>
                  {["CAT", "XAT", "CMAT", "MAT", "SNAP", "NMAT", "MAH-CET", "GMAT", "Other"].map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
                </select>
              </Field>
              <Field label="Exam Score / Percentile"><input value={data.exam_score} onChange={set("exam_score")} className={inputCls} placeholder="e.g. 82 percentile" data-testid="adm-score" /></Field>
              <Field label="Work Experience"><input value={data.work_experience} onChange={set("work_experience")} className={inputCls} placeholder="e.g. 2 years / Fresher" data-testid="adm-workexp" /></Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="admission-step-preferences">
              <div className="rounded-xl border border-brand-cyan/25 bg-brand-cyan/10 px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/50">Applying To</p>
                <p className="mt-0.5 font-display font-semibold text-brand-cyan" data-testid="adm-college">{college}</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Preferred Course">
                  <select value={data.preferred_course} onChange={set("preferred_course")} className={inputCls} data-testid="adm-course">
                    {["MBA", "PGDM"].map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
                  </select>
                </Field>
                <Field label="Intake Year">
                  <select value={data.intake_year} onChange={set("intake_year")} className={inputCls} data-testid="adm-intake">
                    {["2026", "2027"].map((x) => <option key={x} value={x} className="bg-ink-1">{x}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Anything we should know?"><textarea value={data.message} onChange={set("message")} rows={3} className={inputCls} placeholder="Scholarships, hostel, loans..." data-testid="adm-message" /></Field>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/55">
                {data.name} · {data.mobile} · {data.email}{data.graduation ? ` · ${data.graduation}` : ""}{data.entrance_exam ? ` · ${data.entrance_exam}` : ""}
              </div>
            </div>
          )}
        </motion.div>

        <div className="mt-6 flex gap-3">
          {step > 0 && (
            <button type="button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-colors hover:border-brand-cyan/50" data-testid="adm-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          {step < 2 ? (
            <button type="button" onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3 text-sm font-semibold text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-40" data-testid="adm-next">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={onSubmit} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3 text-sm font-semibold text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-60" data-testid="adm-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck className="h-4 w-4" />}
              Submit Application
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}
